import { LevelName, WinType } from "../abstracts/types/enums";
import { IWinLevel } from "../abstracts/types/interfaces";
import { UCSoundNames } from "./assetsLoader/UCAssetsNames";
import { UCSpecialSymbolNames, UCSymbolNames } from "./types/UCEnums";
import {
    AmbientSoundConfig,
    IUCConfig,
    IUCDevDebugSettings,
    IUCDisplayConfig,
    IUCReelAnimation,
    IUCReelConfig,
    IUCSymbolConfig,
} from "./types/UCInterfaces";

export const davidPanningReelStop: number[] = [-0.25, -0.18, 0.0, 0.18, 0.25];

//timeFrame (in seconds) is used to set the time at which the sound will be played.
//for example: when game starts, the first bird sound will be played at a random time between 20 and 30 seconds.
//once the sound event was triggered, the time counter will reset and the next sound will be played
//at other random time between 20 and 30 seconds.
export const ambientalSoundSettings: AmbientSoundConfig[] = [
    {
        name: `bird`,
        timeFrame: [20, 30],
        soundsList: [
            UCSoundNames.AMBIENT_BIRD_01,
            UCSoundNames.AMBIENT_BIRD_02,
            UCSoundNames.AMBIENT_BIRD_03,
            UCSoundNames.AMBIENT_BIRD_04,
        ],
    },
    {
        name: `burp`,
        timeFrame: [80, 140],
        soundsList: [UCSoundNames.AMBIENT_BURP_01, UCSoundNames.AMBIENT_BURP_02],
    },
    {
        name: `can`,
        timeFrame: [30, 50],
        soundsList: [
            UCSoundNames.AMBIENT_CAN_01,
            UCSoundNames.AMBIENT_CAN_02,
            UCSoundNames.AMBIENT_CAN_03,
            UCSoundNames.AMBIENT_CAN_04,
        ],
    },
];

export const winLevels: IWinLevel[] = [
    { name: LevelName.TINY, winType: WinType.TINY, min: 0, max: 1, animationDuration: 0.4 },
    { name: LevelName.SMALL, winType: WinType.SMALL, min: 1, max: 3, animationDuration: 0.53 },
    { name: LevelName.MEDIUM, winType: WinType.MEDIUM, min: 3, max: 5, animationDuration: 0.66 },
    { name: LevelName.LARGE, winType: WinType.LARGE, min: 5, max: 15, animationDuration: 0.8 },
    { name: LevelName.BIG_WIN_1, winType: WinType.BIG, min: 15, max: 25, animationDuration: 6 },
    { name: LevelName.BIG_WIN_2, winType: WinType.BIG, min: 25, max: 50, animationDuration: 6 },
    { name: LevelName.BIG_WIN_3, winType: WinType.BIG, min: 50, max: 100, animationDuration: 6 },
    { name: LevelName.BIG_WIN_4, winType: WinType.BIG, min: 100, max: 250, animationDuration: 6 },
    { name: LevelName.BIG_WIN_5, winType: WinType.BIG, min: 250, max: 50000, animationDuration: 6 },
    {
        name: LevelName.MAX_CAP,
        winType: WinType.MAX_CAP,
        min: 50000,
        max: 52000, //not being used
        animationDuration: 6,
    },
];

export const fishMultipliersMap: Map<UCSpecialSymbolNames, number> = new Map([
    [UCSpecialSymbolNames.F1, 2],
    [UCSpecialSymbolNames.F2, 5],
    [UCSpecialSymbolNames.F3, 25],
    [UCSpecialSymbolNames.F4, 100],
    [UCSpecialSymbolNames.F5, 250],
    [UCSpecialSymbolNames.F6, 1000],
    [UCSpecialSymbolNames.F7, 5000],
]);

export const BASE_REELSET: string[][] = [
    [`M`, `M1`, `L1`, `M2`, `F5`, `F3`],
    [`M`, `BF`, `L2`, `F1`, `F6`, `F4`],
    [`M`, `L3`, `M3`, `F2`, `F7`, `M`],
    [`M`, `BF`, `F7`, `L4`, `S`, `F6`],
    [`F7`, `M5`, `L5`, `F4`, `W`, `M`],
];

export const symbolsArray: string[] = Object.values(UCSymbolNames);

export const symbolConfig: IUCSymbolConfig = {
    symbolWidth: 137,
    symbolHeight: 111,
    symbolSpacing: 0,
};

export const normalConfig: IUCReelAnimation = {
    startSymbolsDelay: 0.05,
    startEase: `circ.in`,
    startMinDelay: 0,
    startReelsDelay: 0.05,
    stopSpinReelDelay: 0.1,
    skipReelsDelay: 0.05,
    skipCurrentSymbolsTimeScale: 3,
    showNearWinAnimationDelay: 0,
    nearWinDuration: 1,
    hideNearWinDelay: 0.4,
    stepDuration: 0.06,
    stepSize: symbolConfig.symbolHeight + symbolConfig.symbolSpacing,
};

export const reelsConfig: IUCReelConfig = {
    reelsNumber: 5,
    rowsNumber: 5,
    reelSpacing: 2,
    reelSpacingOffset: [0, 0, 0, 0, 0],
    viewXPosition: -5,
    viewYPosition: 0,
    reelsContainerX: 20,
    reelsContainerY: 120,
    normalAnimation: normalConfig,
    stepDuration: 0.06,
    stepSize: symbolConfig.symbolHeight + symbolConfig.symbolSpacing,
    visualPaddingBefore: 1,
    paddingBefore: 0,
    visibleSymbols: 3,
    paddingAfter: 0,
    visualPaddingAfter: 1,
};

export const displayConfig: IUCDisplayConfig = {
    gameWidth: 1280,
    gameHeight: 720,
    backgroundWidth: 1680,
    backgroundHeight: 1680,
    reelsViewWidth: 720,
    reelsViewHeight: 720,
};

export const winFontSize: number = 24;
export const boostedName: string = `BOOSTED_BET`;

export const devDebugConfig: IUCDevDebugSettings = {
    disableReelMask: false,
    showReelDebugGraphics: false,
    showSymbolsDebugGraphics: false,
    showTimelineController: false,
    showSoundDebugger: false,
};

export const UCConfig: IUCConfig = {
    reelConfig: reelsConfig,
    displayConfig: displayConfig,
    symbolConfig: symbolConfig,
    devDebugConfig: devDebugConfig,
    symbolsArray: symbolsArray,
};
